/**
 * Calculate a dancer's age in completed years from a date-of-birth string.
 *
 * Why this util exists
 * --------------------
 * The old Meteor app displayed `age` (calculated) on the public profile
 * card. The current Next.js app stores `profiles.date_of_birth` but did
 * not show age anywhere. This util restores that display while honoring
 * the project's timezone-safety rule: a date-only string like
 * `"1995-03-15"` must be parsed as local midnight, not UTC midnight, so
 * users west of UTC don't see an age that's a day off across their
 * birthday.
 *
 * Behavior
 * --------
 * - Returns the number of completed years between `birthdate` and `now`.
 * - Returns `null` for null / undefined / unparseable input.
 * - Returns `null` for future birthdates (defensive — the wizard validates
 *   this, but a corrupted row should not render "-12 years").
 * - Returns `null` for ages > 120 (sanity bound — almost certainly bad
 *   data, e.g. a year typo like `0195`).
 *
 * Accepts ISO date strings (`YYYY-MM-DD`), ISO datetimes
 * (`YYYY-MM-DDTHH:mm:ssZ`), or `Date` objects. Date-only strings are
 * parsed as local midnight to keep the displayed age stable across
 * timezones.
 */
export function calculateAge(
  birthdate: string | Date | null | undefined,
  now: Date = new Date(),
): number | null {
  const parsed = parseLocalBirthdate(birthdate);
  if (!parsed) return null;

  // Future birthdate — corrupt data; don't render a negative age.
  if (parsed.getTime() > now.getTime()) return null;

  let age = now.getFullYear() - parsed.getFullYear();
  const monthDiff = now.getMonth() - parsed.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < parsed.getDate())) {
    age -= 1;
  }

  if (age < 0 || age > 120) return null;

  return age;
}

/**
 * Parse a date-of-birth value. Local-midnight rule for `YYYY-MM-DD`
 * strings (Critical Rule #19). Inlined here so the artists package does
 * not depend on `apps/web/lib/date-utils`.
 */
function parseLocalBirthdate(
  value: string | Date | null | undefined,
): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  // ISO datetime — parse as-is.
  if (value.includes('T') || value.includes(' ')) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Date-only — append local midnight so it isn't interpreted as UTC.
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}
